import React, { useEffect, useMemo, useState } from "react";
import type { CommonPageProps, OrderTableRow } from "../types";
import { OrderChangeModal } from "../components/modal/modalOrderListUpdate";
import { OrderDeleteModal } from "../components/modal/modalOrderListDelete";
import CommonPage from "../components/commonPage";
import { useApi } from "../logic/classApi";
import { useOrderApi } from "../logic/orderApi";
import { useChemicalApi } from "../logic/chemicalApi";
import {
  compareISODatetime,
  displayDatetimeString,
  getInputMonthString,
  getJstWeekday,
} from "../logic/dateUtils";
import OrderTable from "../components/orderTable";
import { useDownloadApi } from "../logic/downloadApi";
import { PAGE, usePermission } from "../logic/permission";
import Loading from "../components/loading";
import useCookie from "../logic/cookie";
import "./css/pageOrderList.css";

/**
 * 発注登録一覧・ダウンロード画面
 * @param 画面表示パラメータ（ヘッダ、サイドバーの表示有無）
 * @returns
 */
const OrderList: React.FC<CommonPageProps> = ({ showHeader, showSidebar, onLogout }) => {
  // 表示権限チェック
  const [permission] = usePermission(PAGE.ORDER);
  if (!permission.canDisplay) {
    // 表示権限がない場合は、表示可能画面に自動遷移させるため、ローディング表示のみを返す
    return <Loading />;
  }
  // 支所コード
  const [cookieData] = useCookie();
  const branchCode = cookieData.branchCode;
  // API呼び出し
  const api = useApi();
  const [orderData, orderHandle] = useOrderApi(api, branchCode);
  const [chemicalData] = useChemicalApi(api, branchCode);
  const [downloadHandle] = useDownloadApi(api, branchCode);
  // 表示年月
  const [yearMonth, setYearMonth] = useState<string>(getInputMonthString(new Date()));
  // 編集中の発注情報
  const [editOrder, setEditOrder] = useState<OrderTableRow | null>(null);
  // 削除中の発注情報
  const [deleteOrder, setDeleteOrder] = useState<OrderTableRow | null>(null);

  // 非同期処理中フラグ
  const [isProc, setProc] = useState(false);
  // 非同期処理実行
  const doProc = (fn: () => Promise<void>) => {
    if (isProc) return;
    setProc(true);
    try {
      fn();
    } finally {
      setProc(false);
    }
  };

  // 発注情報テーブル表示用データ
  const orderTableDatas = useMemo(
    () =>
      orderData.orders
        ?.sort((a, b) => compareISODatetime(a.orderAdddatetime, b.orderAdddatetime))
        .filter(o => chemicalData.isValidChemical(o.chemicalId))
        .map(order => {
          const chemical = chemicalData.getChemical(order.chemicalId);
          const vendor = chemicalData.getVendor(order.chemicalId);
          const [scheduledDate, scheduledTime] = displayDatetimeString(order.orderDatetime);
          const [scheduledDateEnd, scheduledTimeEnd] = displayDatetimeString(order.orderDatetimeEnd);
          const [orderAddDate] = displayDatetimeString(order.orderAdddatetime);
          return {
            branchCode: order.branchCode,
            orderAdddatetime: order.orderAdddatetime,
            chemicalId: order.chemicalId,
            orderAddDate,
            vendorId: vendor?.vendorId,
            vendorName: vendor?.vendorName,
            chemicalName: chemical?.chemicalName,
            scheduledDate,
            scheduledTime,
            scheduledTimeEnd,
            orderVolume: order.orderVolume,
            chemicalUnit1: chemical?.chemicalUnit1,
            chemicalUnit2: chemical?.chemicalUnit2,
            chemicalUnit2Calc: chemical?.chemicalUnit2Calc,
            chemicalViewtype: chemical?.chemicalViewtype,
            hiddenAt: order.hiddenAt,
          } as OrderTableRow;
        }),
    [orderData.orders, chemicalData.isLoaded]
  );

  // 発注情報編集
  const handleOrderEdit = async (date: string, time: string, timeEnd: string, volume: string) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await orderHandle.updateOrder(
        editOrder.chemicalId,
        editOrder.orderAdddatetime,
        date,
        time,
        timeEnd,
        Number(volume)
      );
      await orderHandle.loadOrdersByRegistrationDate(yearMonth);
      setEditOrder(null);
    });
  };

  // 発注情報削除
  const handleOrderDelete = async () => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await orderHandle.deleteOrder(deleteOrder.chemicalId, deleteOrder.orderAdddatetime);
      await orderHandle.loadOrdersByRegistrationDate(yearMonth);
      setDeleteOrder(null);
    });
  };

  // 発注書ダウンロード
  const handleDownload = async (row: OrderTableRow) => {
    doProc(async () => {
      const fileName = `発注書_${row.orderAddDate.replace(/\([^)]*\)$/, "")}_${row.chemicalName}.xlsx`;
      downloadHandle.downloadOrder(
        fileName,
        row.chemicalId,
        row.scheduledDate,
        row.scheduledTime,
        row.scheduledTimeEnd,
        row.orderVolume
      );
    });
  };

  // 薬剤関連データ取得完了 および 登録年月変更時
  useEffect(() => {
    if (yearMonth) orderHandle.loadOrdersByRegistrationDate(yearMonth);
  }, [yearMonth]);

  return (
    <CommonPage showHeader={showHeader} showSidebar={showSidebar} onLogout={onLogout}>
      <div className="app-main">
        <section className="orderList-section">
          <div className="section-header">
            <h3>発注登録一覧・ダウンロード</h3>
          </div>

          {/* 年・月選択 */}
          <div className="parts">
            <label>登録年月：</label>
            <input type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} />
          </div>

          {/* 発注一覧表 */}
          <div>
            <OrderTable
              datas={orderTableDatas}
              onEdit={setEditOrder}
              onDelete={setDeleteOrder}
              onDownload={handleDownload}
              canEdit={permission.canEdit}
            />
          </div>
          {editOrder && (
            <OrderChangeModal
              order={editOrder}
              onClose={() => setEditOrder(null)}
              onEdit={handleOrderEdit}
            />
          )}

          {deleteOrder && (
            <OrderDeleteModal
              order={deleteOrder}
              onClose={() => setDeleteOrder(null)}
              onDelete={handleOrderDelete}
            />
          )}
        </section>
      </div>
    </CommonPage>
  );
};

export default OrderList;
